import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  Tabs,
  Tab,
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { PlusCircleFill, Search } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import './pending-leave.css';

/* -------------------------------------------------
   Tiny badge component – keeps the JSX tidy
------------------------------------------------- */
const StatusBadge = ({ status }) => {
  const badgeClass = `pl-status-badge pl-status-${status.toLowerCase()}`;
  return <span className={badgeClass}>{status}</span>;
};

/* -------------------------------------------------
   Main component
------------------------------------------------- */
const PendingLeaveEmp = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [key, setKey] = useState('all');
  const [userId, setUserId] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  /* ---- fetch userId from localStorage ---- */
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      setError('User ID not found in localStorage. Please log in again.');
      setLoading(false);
    }
  }, []);

  /* ---- fetch leaves when userId is ready ---- */
  useEffect(() => {
    if (!userId) return;

    const fetchLeaves = async () => {
      try {
        const { data } = await axios.get(
          `https://backend.ems.hiradentallab.com/api/leavesmanual/leaves/${userId}`
        );
        if (data.success) {
          setLeaves(data.leaves);
        } else {
          setError(data.message || 'Failed to fetch leaves');
        }
      } catch (err) {
        setError(err.message || 'Error fetching leaves');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaves();
  }, [userId]);

  /* ---- filtering ---- */
  const filteredLeavesByTab = leaves.filter((leave) => {
    if (key === 'all') return true;
    return leave.status === key;
  });

  const filteredLeaves = filteredLeavesByTab.filter(
    (leave) =>
      leave.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ---- helpers ---- */
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const handleShow = (leave) => {
    setSelectedLeave(leave);
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setSelectedLeave(null);
  };

  /* ---- render ---- */
  if (loading) return <div className="pl-loading">Loading...</div>;
  if (error) return <div className="pl-error">{error}</div>;

  return (
    <Container fluid className="pl-leave-dashboard">
      {/* Header */}
      <Row className="pl-header-row align-items-center justify-content-between">
        <Col>
          <h1 className="pl-dashboard-title">Leave Management Dashboard</h1>
          <p className="pl-dashboard-subtitle">
            Track and manage your leave records throughout the academic year
          </p>
        </Col>
        <Col xs="auto">
          <Link to="/add-leave" className="pl-add-leave-icon">
            <PlusCircleFill size={32} /> Add Leave
          </Link>
        </Col>
      </Row>

      {/* Tabs + Search */}
      <Row className="pl-controls-row mb-4 align-items-center">
        <Col md={6}>
          <Tabs
            id="leave-tabs"
            activeKey={key}
            onSelect={(k) => setKey(k)}
            className="pl-leave-tabs"
          >
            <Tab eventKey="all" title="All" />
            <Tab eventKey="pending" title="Pending" />
            <Tab eventKey="approved" title="Approved" />
            <Tab eventKey="rejected" title="Rejected" />
          </Tabs>
        </Col>
        <Col md={6} className="d-flex justify-content-end">
          <InputGroup className="pl-search-bar">
            <Form.Control
              type="text"
              placeholder="Search by reason or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text>
              <Search size={20} />
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Row>

      {/* Table */}
      <Table className="pl-leave-table">
        <thead>
          <tr>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Allowance Type</th>
            <th>Reason</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredLeaves.length > 0 ? (
            filteredLeaves.map((leave) => (
              <tr key={leave._id}>
                <td>{formatDate(leave.startDate)}</td>
                <td>{formatDate(leave.endDate)}</td>
                <td>{leave.allowanceType}</td>
                <td>
                  {leave.reason && leave.reason.length > 15
                    ? `${leave.reason.substring(0, 15)}...`
                    : leave.reason}
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="pl-read-more-btn ms-2"
                    onClick={() => handleShow(leave)}
                  >
                    Read More
                  </Button>
                </td>

                {/* ← Fancy status badge */}
                <td>
                  <StatusBadge status={leave.status} />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="pl-no-data">
                No leaves found
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="pl-modal-header">
          <Modal.Title>Leave Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pl-modal-body">
          {selectedLeave && (
            <>
              <p>
                <strong>Start Date:</strong>{' '}
                {formatDate(selectedLeave.startDate)}
              </p>
              <p>
                <strong>End Date:</strong>{' '}
                {formatDate(selectedLeave.endDate)}
              </p>
              <p>
                <strong>Allowance Type:</strong>{' '}
                {selectedLeave.allowanceType}
              </p>
              <p>
                <strong>Reason:</strong> {selectedLeave.reason}
              </p>
              <p>
                <strong>Status:</strong>{' '}
                <StatusBadge status={selectedLeave.status} />
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="pl-modal-footer">
          <Button
            variant="secondary"
            onClick={handleClose}
            className="pl-close-btn"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PendingLeaveEmp;